import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Button, Stack, InputAdornment, Drawer, Chip } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTheme } from "@mui/material/styles";
import CustomDropdown from 'components/Dropdown';
import AdvancedBuilder from './AdvancedBuilder';
import { dropdownSxProps } from './utilities';
import CustomTextField from 'components/Inputs/CustomTextField';
import { formatQuery } from 'react-querybuilder';
import { useParams } from 'react-router-dom';
import QuerySearchHistory, { queryPrefOptions } from 'components/FineOps/ResourceMapping/QuerySearchHistory';
import SavedQueriesModal from 'components/FineOps/ResourceMapping/SavedQueriesModal';
import SaveAsModal from 'components/FineOps/ResourceMapping/SaveAsModal';
import { InfoTooltip } from 'components/Widgets/InfoTooltip';
import { ReactComponent as HistoryIcon }  from '../../assets/history_24_blue.svg';
import { ReactComponent as SaveBlueIcon }  from '../../assets/save_24_blue.svg';
import { ReactComponent as LinkBlueIcon }  from '../../assets/link_24_blue.svg';
import { ReactComponent as DropdownBlueIcon }  from '../../assets/dropdown.svg';
import ProjectAssociationModal from 'components/FineOps/ResourceMapping/ProjectAssociationModal';
import { fetchSavedQueryList, saveSearchedQuery } from 'service/service';
import MoreOptionMenu from 'components/Dropdown/MoreOptionSecondary';
import { ReactComponent as UnlinkGreyIcon }  from '../../assets/unlink_24_grey.svg';
import { ReactComponent as UnlinkRedIcon }  from '../../assets/unlink_24_red.svg';
import { ReactComponent as SaveBlackIcon }  from '../../assets/save_24_black.svg';
import { ReactComponent as SavedBlackIcon }  from '../../assets/PepWise2.0/all-saved-black.svg';
import { ReactComponent as SavedBlueIcon }  from '../../assets/PepWise2.0/all-saved-blue.svg';
import { ReactComponent as LinkGreyIcon }  from '../../assets/link_24_grey.svg';
import eventEmitter from 'SnackBarEventEmitter';
import InfoIcon from '@mui/icons-material/Info';
import Loader from 'components/Loader/LoaderFile';
import CustomSelectDropdown from 'components/Dropdown/CustomSelectDropdown';
import TotalCostBadge from 'components/FineOps/ResourceMapping/TotalCostBadge';
import { computeCostDisplay } from 'components/FineOps/ResourceMapping/ResourceMappingHelper';

const initialQuery = {
	id: '660802c2-f66d-46ed-a996-24962e922680',
	rules: [
	]
};

const initialClearQuery = {
	id: '660802c2-f66d-46ed-a996-24962e922680',
	rules: [
		{
            field: '',
            operator: '',
            value: ''
        },
	]
};

interface QueryHistory {
	query: any;
	fields: string[];
	timestamp: any; 
	rawQuery: any; 
	selectedCheckbox: any[];
	selGroupPaths: any[];
}

const PepWiseQueryBuilder = ({
	fields,
	queryPreferenceOptions,
	queryPreference,
	setQueryPreference,
	setQueryProps,
	loadOptionsForField,
	fieldValueOptionsCallback,
	valueField,
	setTotalPages,
	sortingInfo,
	setSortingInfo,
	setIsQueryPrefChange,
	setIsReapplied,
	isProjectDetails = false,
	isReapplied,
	savedQuery = {},
	setSavedQuery,
	hasResourceList = false,
	setHasResourceList,
	setShouldFetch,
	handleUnlinkProject,
	loadOptionsForOperator,
	abortControllerRef,
	setIsAbort,
	isAbort,
	resourceTypeCost = []
}: any) => {
	const theme= useTheme();
	const colors = theme.palette;
	const [isAdvancedBuilder, ] = useState(true);
	const [query, setQuery] = useState<any>(initialQuery);
	const [searchQuery, setSearchQuery] = useState<any>('');
	const [queryPreferenceVal, setQueryPreferenceVal] = useState('Resource_flat');
	const [isLoading, setIsLoading] = useState(false);
	const [isFormValid, setIsFormValid] = useState(true);
	const [localFields, setLocalFields] = useState<typeof fields>({});
	const [isQuerySearchHistoryOpen, setIsQuerySearchHistoryOpen] = useState<boolean>(false);
	const [isSavedQueriesModalOpen, setIsSavedQueriesModalOpen] = useState<boolean>(false);
	const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState<boolean>(false);
	const [savedQueriesCount, setSavedQueriesCount] = useState<number>(0);
	const [isProjectAssocModalOpen, setIsProjectAssocModalOpen] = useState<boolean>(false);
	const [selectedCheckbox, setSelectedCheckbox] = useState<any>([]);
	const [selGroupPaths, setSelGroupPaths] = useState<number[][]>([]);
	const [queryId, setQueryId] = useState<number>(-1);
	const { programId, projectId, program, project } = useParams();
	const [commitValue, setCommitValue] = useState<any>('commit');
	const [, setAllocationFlag] = useState<any>(0);
	const [isHistoryQuery, setIsHistoryQuery] = useState(false);
	const [selectedValues, setSelectedValues] = useState({
		program: '',
		project: ''
	});

	const costDisplay = useMemo(() => {
		if (isProjectDetails) {
			return null;
		}

		return computeCostDisplay(
			queryPreferenceVal,
			resourceTypeCost?.breakdown ?? resourceTypeCost ?? []
		);
	}, [isProjectDetails, queryPreferenceVal, resourceTypeCost]);

	const stackRef = useRef<HTMLDivElement | null>(null);
	const [height, setHeight] = useState<number | undefined>(undefined); // initial height
	const [isResized, setIsResized] = useState(false);

	const startResize = (e: React.MouseEvent) => {
		e.preventDefault();
		const startY = e.clientY;
		const startHeight = stackRef.current?.offsetHeight || 200;

		const doDrag = (moveEvent: MouseEvent) => {
			const newHeight = startHeight + (moveEvent.clientY - startY);
			const clampedHeight = Math.max(200, newHeight);

			if (clampedHeight <= 200) {
				setIsResized(true); 
				setHeight(undefined); // reset to auto
			} else {
				setIsResized(false); 
				setHeight(clampedHeight);
			}
		};

		const stopDrag = () => {
			document.removeEventListener('mousemove', doDrag);
			document.removeEventListener('mouseup', stopDrag);
		};

		document.addEventListener('mousemove', doDrag);
		document.addEventListener('mouseup', stopDrag);
	};

	const validateForm = () => {
		const hasEmptyField = (rules: any[]): boolean => {
			return rules.some((rule) => {
				if (typeof rule !== 'object') return false;
				if (Array.isArray(rule.rules)) return hasEmptyField(rule.rules);
				return !rule.field || !rule.operator || (rule.operator !== 'null' && rule.operator !== 'notNull' && !rule.value);
			});
		};
		
		if(query.rules.length === 0)
			setIsFormValid(false);
		else
			setIsFormValid(!hasEmptyField(query.rules));
	};

	function getQueryPrefLabel(queryPreference: string) {
		const match = queryPrefOptions.find(option => option.value === queryPreference);
		return match ? match.label : '';
	};

	const setQueryValue = () => {
		const filteredRules = query.rules.map((rule: { field: string; operator: string; value: string; id: string }) => {
			if (rule.field === "") {
				return { field: "1", operator: "=", value: "1", id: rule.id };
			}
			return rule;
		});
		const values = formatQuery({ ...query, rules: filteredRules }, 'parameterized');
		return values;
	}

	const allocateResource = async (selValue: any) => {
		try {
			let formPayload: any;
			setIsLoading(true);
			if(selValue === 'commit') {
				const fieldsStr: any = localStorage.getItem(`resource-details_user_col_pref`);
				const fields = fieldsStr ? JSON.parse(fieldsStr) : [];
				const queryPrefVal = getQueryPrefLabel(queryPreferenceVal);
				const queryParam = setQueryValue();
				formPayload = {
					queryName: `${program}|${project}|${queryPrefVal}`,
					query: queryParam,
					queryJson: query,
					selectedColumns: fields,
					queryPreference: queryPreferenceVal,
					selectedPaths: selectedCheckbox,
					groupPaths: selGroupPaths,
					program: programId,
					project: projectId,
					isAllocated: 1, 
					queryId: queryId
				};
			}
			else {
				formPayload = {
					program: programId,
					project: projectId,
					commitAll: true
				};
			}
			await saveSearchedQuery(formPayload);

			getSavedQueryList(queryPreferenceVal);
			if(hasResourceList)
				if (typeof setShouldFetch === 'function') setShouldFetch(true);
		} catch (err) {
			console.error(err);
			if (typeof err === 'object' && err !== null && 'response' in err) {
				const error = err as { response: { status: number } };
				if (error.response.status === 503)
					eventEmitter.emit('showSnackbar', 'Service temporarily unavailable. Please try again later.', 'info');
				else if (error.response.status === 504) eventEmitter.emit('showSnackbar', 'Gateway Timeout', 'info');
			}
		}
		finally {
			setIsLoading(false);
		}
	};

	const handleAbortClick = () => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort(); 
		}
		setIsAbort(false);
	}
	const handleRunQuery = () => {
		setIsAbort(true);
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		abortControllerRef.current = new AbortController();
		setSortingInfo({ sortBy: '', sortDir: '' });
		setTotalPages(0);
		setQueryPreference(queryPreferenceVal);
		// Replace rules with empty field values with a default condition (1 = 1)
		const filteredRules = query.rules.map((rule: { field: string; operator: string; value: string; id: string }) => {
			if (rule.field === "") {
				return { field: "1", operator: "=", value: "1", id: rule.id };
			}
			return rule;
		});
		const formattedQuery = isAdvancedBuilder ? formatQuery({ ...query, rules: filteredRules }, 'parameterized') : searchQuery;
		setQueryProps({
			isSearch: !isAdvancedBuilder,
			query: formattedQuery
		});
		if (typeof setShouldFetch === 'function') {
			setShouldFetch(true);
		}
		const storageKey = isProjectDetails ? 'resource-details_user_col_pref' : 'resource-list_user_col_pref';
		const fieldsStr = localStorage.getItem(storageKey);
		const fields = fieldsStr ? JSON.parse(fieldsStr) : [];
		saveQueryToLocal({query: formattedQuery, fields: fields, rawQuery: query, queryPreference: queryPreferenceVal});
	};

	const MAX_HISTORY = 25;
	const STORAGE_KEY = 'query_search_history';
	const saveQueryToLocal = (queryData: {
		query: any;
		fields: string[];
		rawQuery: any;
		queryPreference: any;
	}) => {
		const localTimestamp = new Date().toString(); 
		const newEntry: QueryHistory = {
			...queryData,
			selectedCheckbox: selectedCheckbox,
			selGroupPaths: selGroupPaths,
			timestamp: localTimestamp,
		};

		const searchQuery: any = localStorage.getItem(STORAGE_KEY);
		let existingHistory: QueryHistory[] = [];
		existingHistory = searchQuery ? JSON.parse(searchQuery) : [];
		if(!!searchQuery) {
			existingHistory = existingHistory.filter(
				(item) =>
					!(item.query.sql === newEntry.query.sql &&
					JSON.stringify(item.query.params) === JSON.stringify(newEntry.query.params) &&
					JSON.stringify(item.fields) === JSON.stringify(newEntry.fields))
			);
		}

		let updatedHistory = existingHistory ? [newEntry, ...existingHistory] : [newEntry];
		if (updatedHistory.length > MAX_HISTORY) 
			updatedHistory = updatedHistory.slice(0, MAX_HISTORY); // FIFO trim

		localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedHistory));
	};

	const handleClear = () => {
		if(isProjectDetails) {
			setHasResourceList(false);
			setSavedQuery({});
		} 
		setQuery(initialClearQuery);
		setIsReapplied(false);
		setSelectedCheckbox([]);
		setSelGroupPaths([]);
	};

	async function getSavedQueryList(queryVal: any) {
		try {
			let res = await fetchSavedQueryList('', queryVal, programId, projectId);
			if (res.success) {
				if(res.data.length === 0) handleClear();
				else if(res.data) {
					const formattedData = res.data.map((item: any) => ({
						...item,
						queryJson: JSON.parse(item.queryJson),
						selectedPaths: JSON.parse(item.selectedPaths),
						groupPaths: JSON.parse(item.groupPaths),
						param: item.param.split(','),
						cost:JSON.parse(item.costBreakdown)
					}));
					setSavedQuery(formattedData[0]);
					setQuery(formattedData[0].queryJson);
					setSelectedCheckbox(formattedData[0].selectedPaths); 
					setSelGroupPaths(formattedData[0].groupPaths); 
				}
			}
		} catch (error) {
			console.error(error);
		} 
	}

	// Query Preference Change
	const handleQueryPrefOnChange = (e: any) => {
		setIsQueryPrefChange(true);
		setQueryPreferenceVal(e.target.value);
		if(isProjectDetails) getSavedQueryList(e.target.value);
	}
	

	// saved query
	const handleSaveSearchQuery = () => {
		setIsSaveAsModalOpen(true);
	};

	const handleAllSavedQueries = () => {
		setIsSavedQueriesModalOpen(true);
	};

	async function getAllSavedQueriesCount() {
		try {
			let res = await fetchSavedQueryList('', '', '', '');
			if (res.success) {
				setSavedQueriesCount(res.data?.length || 0);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const handleLinkSearchedQuery = async () => {
		try {
			setIsLoading(true);
			let result;
			const storageKey = 'resource-details_user_col_pref';
			const fieldsStr = localStorage.getItem(storageKey);
			const fields = fieldsStr ? JSON.parse(fieldsStr) : [];
			const queryPrefVal = getQueryPrefLabel(queryPreferenceVal);
			const queryParam = setQueryValue();
			const formPayload = {
				queryName: `${program}|${project}|${queryPrefVal}`,
				query: queryParam,
				queryJson: query,
				selectedColumns: fields,
				queryPreference: queryPreferenceVal,
				selectedPaths: selectedCheckbox,
				groupPaths: selGroupPaths,
				program: programId,
				project: projectId,
				queryId: queryId,
				isSaveQuery: true,
				isProjectDetails: isProjectDetails
			};
			result = await saveSearchedQuery(formPayload);
			if(result.success) {
				getSavedQueryList(queryPreferenceVal);
			}
		} catch (err) {
			console.error(err);
		}
		finally {
			setIsLoading(false);
		}
	};

	// Link query to project
	const handleProjectAssociation = () => {
		if(!!isProjectDetails)
			handleLinkSearchedQuery();
		else
			setIsProjectAssocModalOpen(true);
	}
	
	// Unlink query to project
	const handleUnlinkProjectClick = () => {
		handleUnlinkProject(savedQuery);
	}

	const handleDetailsThreeDotsClick = (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();
	};
	const handleDetailsThreeDotsClose = (event: any) => {
		event.preventDefault();
		event.stopPropagation();
	};

	// fetch query search history
	const handleQuerySearchHistory = () => {
		setIsQuerySearchHistoryOpen(true);
	};

	const handleFieldChange = (fieldKey: string) => {
		if (loadOptionsForField) {
		  loadOptionsForField(fieldKey);
		}
	};
	
	const handleOperatorChange = (operator: string, fieldKey: string) => {
		if (loadOptionsForOperator) {
		  loadOptionsForOperator(operator, fieldKey);
		}
	};

	const handleCommitChange = (event: any) => {
		const value = event?.target.value;
		setCommitValue(value);
		allocateResource(value);
	};

	useEffect(() => {
		validateForm();
		setIsResized(false);
	}, [query]);

	// project details page query load
	useEffect(() => {
		if(isProjectDetails && savedQuery && Object.keys(savedQuery).length > 0) {
			setQuery(savedQuery.queryJson);
			setQueryPreferenceVal(savedQuery.queryPreference);
			setSelectedCheckbox(savedQuery.selectedPaths); 
			setSelGroupPaths(savedQuery.groupPaths); 
			setQueryId(savedQuery.queryId);
			setAllocationFlag(savedQuery.isAllocated);
			localStorage.setItem(`resource-details_user_col_pref`, JSON.stringify(savedQuery.selectedColumns));
		}
		else {
			setQueryId(-1);
			if(isProjectDetails && !isHistoryQuery) setQuery(initialClearQuery);
		}
		setIsHistoryQuery(false);
	}, [isProjectDetails, savedQuery]);

	useEffect(() => {
		if (!fields || Object.keys(fields).length === 0) return;
		let omitKeys: string[] = [];
		switch (queryPreferenceVal) {
			case 'Resource_flat':
				omitKeys = ['ClusterJobSource', 'ClusterName', 'SqlEndpointId'];
				break;
			case 'SqlEndpointId_tree':
				omitKeys = ['ClusterJobSource', 'ClusterName'];
				break;
			case 'ClusterName_inttree':
			case 'ClusterJobSource_jobtree':
				omitKeys = ['SqlEndpointId'];
				break;
			default:
				break;
		}
	
		const filteredFields = Object.keys(fields).reduce((acc: typeof fields, key) => {
			if (!omitKeys.includes(key)) {
				acc[key] = fields[key];
			}
			return acc;
		}, {} as typeof fields);
	
		setLocalFields(filteredFields);
	}, [fields, queryPreferenceVal]);
	

	useEffect(() => {
		getAllSavedQueriesCount();
	}, []);
	
	const isDisabled = useMemo(() => {
		if ((!searchQuery && !isAdvancedBuilder) || query.rules.length === 0 && isAdvancedBuilder) {
			return true;
		}
		return false;
	}, [query, searchQuery, isAdvancedBuilder]);

	const statusLabel = savedQuery.isAllocated ? 'YES' : 'NO';
	const statusColor = savedQuery.isAllocated ? 'success' : 'secondary';
	const stackHeight = isResized ?  '200px' : 'auto';
	const renderCommitStatus = () => {
		if (!isProjectDetails) return null;

		return (
			<>
				<Typography sx={{color: '#71758F', fontWeight: 500}}>Commit Status</Typography>
				<Chip
					label={((!isFormValid && isAdvancedBuilder && Object.keys(savedQuery).length === 0) 
						|| (!!isProjectDetails && Object.keys(savedQuery).length === 0)) ? 'NA' : 
						(statusLabel)}
					color= {((!isFormValid && isAdvancedBuilder && Object.keys(savedQuery).length === 0) 
						|| (!!isProjectDetails && Object.keys(savedQuery).length === 0)) ? 'default' : 
						(statusColor)}
					sx={{
						height: '28px !important',
						width: '80px !important',
						fontSize: '0.75rem',
						marginBottom: '0px !important',
						backgroundColor: ((!isFormValid && isAdvancedBuilder && Object.keys(savedQuery).length === 0) 
							|| (!!isProjectDetails && Object.keys(savedQuery).length === 0)) ? '#A9ADCB !important' : undefined, 
						color: ((!isFormValid && isAdvancedBuilder && Object.keys(savedQuery).length === 0) 
							|| (!!isProjectDetails && Object.keys(savedQuery).length === 0)) ? '#fff !important' : undefined,
						borderRadius: '999px',
						'& .MuiChip-label': {
							fontSize: '12px',
						},
					}}
				/>
			</>
		);
	};

	return (
		<>
		<Stack 
		ref={stackRef}
			sx={{
				backgroundColor: `${colors.queryBuilder.background} !important`,
				color: 'white.main',
				p: 2,
				'& .query-rule-row-combinator-dropdown': {
					position: 'relative',
					...(isProjectDetails ? {} : { bgcolor: 'primary.main' })
				},
				'& .query-rule-row-head-icon': {
					zIndex: 2,
					...(isProjectDetails ? {} : { bgcolor: 'primary.main' })
				},
				overflow: 'hidden',
				minHeight: '200px',
				height: height ? `${height}px` : stackHeight,
    			position: 'relative',
			}}
		>
			{isLoading && <Loader />}
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={1}
				justifyContent="space-between"
				alignItems="center"
				pb={1}
				borderBottom={`0.5px solid ${colors.dark.dark}`}
			>
				<Box display="flex" gap={4} alignItems="center">
					<Typography sx={{color:`${colors.queryBuilder.text} !important`, fontWeight: 500}}>Query Preferences</Typography>
					<CustomSelectDropdown
						options={queryPreferenceOptions.map((item: any) => ({
							value: item.value,
							label: item.label
						}))}
						handleChange={(e) => {handleQueryPrefOnChange(e)}}
						value={queryPreferenceVal}
						boxProps={{
							maxWidth: 400,
							sx: {
								zIndex: 10,
								backgroundColor: `${colors.queryBuilder.dropdown.background} !important`,
								minWidth: '500px !important',
								borderRadius: '4px !important',
							}
						}}
					/>
					{renderCommitStatus()}
				</Box>
				<Box display="flex" gap={0.5} alignItems="center">
					{/* Total Cost (YTD) badge */}
					{!isProjectDetails && <TotalCostBadge costDisplay={costDisplay} />}
					{savedQuery?.cost && Array.isArray(savedQuery.cost) && savedQuery.cost.length > 0 && (
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<InfoTooltip
							 tooltipProps={{
								slotProps: {
									tooltip: {
										sx: {
										backgroundColor: '#EFF6FF',
										width: 214,
										display: 'flex',
										flexDirection: 'column',
										boxSizing: 'border-box',
										gap: '10px',
										},
									},
									},
								}}
								icon={<InfoIcon  sx={{ color: '#A9ADCB', fontSize: '16px', width: '24px', height:'24px' }} />}
							    hasNoCursor =  {true}
								label={[
									{
										title: 'Costs',
										titleSx: {
											fontFamily: 'Montserrat',
											fontSize: '18px',
											fontWeight: 600,
											color:'#000'
										},
										description: (
											<Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
												{savedQuery.cost.map((item: any) => (
													<Box key={`${item?.resourcetype || 'Cost'}-${item?.totalcost ?? 0}`} component="div">
														<Box component="div" sx={{
																				fontFamily: 'Montserrat',
																				fontWeight: 600,
																				fontSize: '14px',
																				lineHeight: '100%',
																				letterSpacing: '0%',
																				color:'#000'
																			}}>
															{item?.resourcetype || 'Cost'}
														</Box>
														<Box component="div" sx={{
																				fontFamily: 'Montserrat',
																				fontWeight: 400,
																				fontSize: '14px',
																				lineHeight: '100%',
																				letterSpacing: '0%',
																				color:'#000'
																			}}>
														${Number(item?.totalcost ?? 0).toLocaleString('en-US', {minimumFractionDigits: 3,     maximumFractionDigits: 3,   })}
														</Box>
													</Box>
												))}
											</Box>
										),
									},
								]}
							/>
						</Box>
					)}
					{isProjectDetails && <CustomDropdown
						value={commitValue}
						// disabled={(!isFormValid && isAdvancedBuilder)}
						handleChange={handleCommitChange}
						options={[
							{ value: 'commit', label: 'COMMIT', isDisabled: (!isFormValid && isAdvancedBuilder) },
							{ value: 'commitAll', label: 'COMMIT ALL'}
						]}
						boxProps={{
							maxWidth: 400,
							sx: {
								backgroundColor: '#009BD8 !important',
								minWidth: '120px !important',
								borderRadius: '4px !important',
							}
						}}
						menuProps={{
							PaperProps: {
							sx: {
								backgroundColor: '#009BD8  !important', 
								'& .MuiList-root': {
									backgroundColor: '#009BD8  !important',
									padding: 0, 
								},
							}}
						}}
						selectComponentProps={{
							sx: {
								...dropdownSxProps,
								height: '32px !important',
								borderRadius: '4px !important',
								"& .MuiOutlinedInput-root": {
									borderRadius: '4px !important'
								},
								"& .MuiOutlinedInput-notchedOutline": {
									borderRadius: '4px !important'
								},
								"& .MuiSelect-icon": {
									color: '#ffffff !important',
									fill: '#ffffff !important',
									top: 'calc(50% - 14px)'
								},
								"& .MuiSelect-icon path": {
									fill: '#ffffff !important'
								}
							}
						}}
						{...(isProjectDetails && { dropdownIcon: DropdownBlueIcon })}
						dropDownStyle={{ marginRight: '8px', marginLeft: '-1px' }}
					/>}
				</Box>
			</Stack>
			{isAdvancedBuilder ? (
				<>
					{Object.keys(localFields).length > 0 && (
						<AdvancedBuilder
							valueField={valueField}
							fields={localFields}
							query={query}
							setQuery={setQuery}
							handleFieldChange={handleFieldChange}
							fieldValueOptionsCallback={fieldValueOptionsCallback}
							onCheckboxChange={setSelectedCheckbox} 
							isReapplied = {isReapplied}
							selectedCheckbox = {selectedCheckbox}
							onGroupPathsChange = {setSelGroupPaths}
							selGroupPaths = {selGroupPaths}
							handleClear = {handleClear}
							isClearDisabled = {isDisabled}
							handleOperatorChange={handleOperatorChange}
							parentHeight={height} 
							isProjectDetails={isProjectDetails}
						/>
					)}
				</>
				// </TableContainer>
			) : (
				<Box mt={2}>
					<CustomTextField
						placeholder="Quick Search"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search sx={{ color: 'white.main' }} />
								</InputAdornment>
							),
							sx: {
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: 'dark.dark',
									borderWidth: '1px',
									borderStyle: 'solid'
								},
								'&:hover .MuiOutlinedInput-notchedOutline': {
									borderColor: 'dark.dark'
								},
								'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
									borderColor: 'dark.dark'
								}
							}
						}}
					/>
				</Box>
			)}
			<Stack direction="row" justifyContent="flex-end" pr={2} pt={2} borderTop={`0.5px solid ${colors.dark.dark}`}>
				{!isAbort ? <Button
					sx={{border: '1px solid #009BD8',color: 'info.main',fontWeight: '700',
						textTransform: 'uppercase', width: 130, height: 31,
						...(isProjectDetails && {
							'&.Mui-disabled': {
								color: '#009BD8 !important',
								opacity: 0.5
							}
						})}}
					variant="pepwiseSecondaryOutline2"
					disabled={(!isFormValid && isAdvancedBuilder)} 
					onClick={handleRunQuery}
				>
					Run Query
				</Button>
				: <Button
					sx={{border: '1px solid #009BD8',color: 'info.main',fontWeight: '700',
						textTransform: 'uppercase', width: 130, height: 31,
						...(isProjectDetails && {
							'&.Mui-disabled': {
								color: '#009BD8 !important',
								opacity: 0.5
							}
						})}}
					variant="pepwiseSecondaryOutline2"
					disabled={(!isFormValid && isAdvancedBuilder)} 
					onClick={handleAbortClick}
				>
					Abort
				</Button>}
				<MoreOptionMenu sx={{ borderRadius: '4px',  border: 'none', 
					color: 'info.main', width: 50, height: 30, ml: 1 }} 
					isAdvance
					isProjectDetails={isProjectDetails}
					onClick={handleDetailsThreeDotsClick}
  					onClose={handleDetailsThreeDotsClose}
					keysWithBorder={['link']}
					menuItems={[
						...(!isProjectDetails
						? [
							{
								key: 'saveAs',
								label: 'Save As',
								icon: <SaveBlackIcon />,
								hoverIcon: <SaveBlueIcon />,
								onMenuClick: handleSaveSearchQuery,
								color: '#000000',
							},
							{
								key: 'allSavedQueries',
								label: 'All Saved Queries',
								icon: <SavedBlackIcon />,
								hoverIcon: <SavedBlueIcon />,
								onMenuClick: handleAllSavedQueries,
								color: '#000000',
								count: savedQueriesCount
							}
						]
						: []),
						{
							key: !!isProjectDetails ? 'projectLink' :  'link',
							label: 'Link',
							icon: <LinkBlueIcon />,
							hoverIcon: <LinkBlueIcon />,
							onMenuClick: handleProjectAssociation,
							color: '#009BD8',
							disabled: (!isFormValid && isAdvancedBuilder)
						},
						...(!!isProjectDetails
						? [
							{
								key: 'unlink',
								label: 'Unlink',
								icon: <UnlinkGreyIcon />,
								hoverIcon: <UnlinkRedIcon />,
								onMenuClick: handleUnlinkProjectClick,
								color: isProjectDetails ? '#FE2626' : undefined,
								disabled: !!isProjectDetails && (Object.keys(savedQuery).length === 0 
								|| (Object.keys(savedQuery).length > 0 && (savedQuery.program === '0' || savedQuery.program === '')))
							}
						]
						: []),
						...(!isProjectDetails
						? [
							{
								key: 'viewHistory',
								label: 'View History',
								icon: <HistoryIcon />,
								hoverIcon: <HistoryIcon />,
								onMenuClick: handleQuerySearchHistory
							}
						]
						: []),
					]}
					>
				</MoreOptionMenu>
			</Stack>
			<Box
				onMouseDown={startResize}
				sx={{
					position: 'absolute',
					bottom: 0,
					left: 0,
					height: '8px',
					width: '100%',
					cursor: 'row-resize',
					zIndex: 999,
				}}
			/>
		</Stack>
		{/* Recent Query Search History */}
		<Drawer anchor="right" open={isQuerySearchHistoryOpen} onClose={() => setIsQuerySearchHistoryOpen(false)} 
			sx={{
				'& .MuiDrawer-paper': {
				width: '100%',
				maxWidth: 500, 
				backgroundColor: `${colors.overlay.background} !important`,
				},
			}}>
			<QuerySearchHistory setIsQuerySearchHistoryOpen={setIsQuerySearchHistoryOpen} setQuery = {setQuery} 
			setIsReapplied = {setIsReapplied} setQueryPreference = {setQueryPreferenceVal} 
			setSelectedCheckbox = {setSelectedCheckbox} setSelGroupPaths = {setSelGroupPaths} 
			isProjectDetails = {isProjectDetails} setSavedQuery = {setSavedQuery} setIsHistoryQuery = {setIsHistoryQuery}/>
		</Drawer>
		{/* Save As */}
		<Drawer anchor="right" open={isSaveAsModalOpen} onClose={() => setIsSaveAsModalOpen(false)} 
			sx={{
				'& .MuiDrawer-paper': {
				width: '100%',
				maxWidth: 500,
				backgroundColor: `${colors.overlay.background} !important`,
				},
			}}>
			<SaveAsModal setIsSaveAsModalOpen={setIsSaveAsModalOpen} query = {query} 
			queryPreference = {queryPreferenceVal} selectedCheckbox = {selectedCheckbox} 
			selGroupPaths = {selGroupPaths} isProjectDetails = {isProjectDetails}
			onSavedQueriesChange = {getAllSavedQueriesCount}/>
		</Drawer>
		{/* Saved Query */}
		<Drawer anchor="right" open={isSavedQueriesModalOpen} onClose={() => setIsSavedQueriesModalOpen(false)} 
			sx={{
				'& .MuiDrawer-paper': {
				width: '100%',
				maxWidth: 500, 
				backgroundColor: `${colors.overlay.background} !important`,
				},
			}}>
			<SavedQueriesModal setIsSavedQueriesModalOpen={setIsSavedQueriesModalOpen} query = {query} 
			setQuery = {setQuery} setIsReapplied = {setIsReapplied} queryPreference = {queryPreferenceVal} 
			setQueryPreference = {setQueryPreferenceVal} selectedCheckbox = {selectedCheckbox} 
			setSelectedCheckbox = {setSelectedCheckbox} selGroupPaths = {selGroupPaths} 
			setSelGroupPaths = {setSelGroupPaths} isProjectDetails = {isProjectDetails}
			selectedValues = {selectedValues} setSelectedValues = {setSelectedValues} setSavedQuery = {setSavedQuery}
			onSavedQueriesChange = {getAllSavedQueriesCount}/>
		</Drawer>
		{/* Link query to project */}
		<Drawer anchor="right" open={isProjectAssocModalOpen} onClose={() => setIsProjectAssocModalOpen(false)} 
			sx={{
				'& .MuiDrawer-paper': {
				width: '100%',
				maxWidth: 490, 
				backgroundColor: `${colors.overlay.background} !important`,
				},
			}}>
			<ProjectAssociationModal setIsProjectAssocModalOpen={setIsProjectAssocModalOpen} query = {query} 
			setQuery = {setQuery} setIsReapplied = {setIsReapplied} queryPreference = {queryPreferenceVal} 
			selectedCheckbox = {selectedCheckbox} selGroupPaths = {selGroupPaths}/>
		</Drawer>
		</>
	);
};

export default PepWiseQueryBuilder;
